import { CloudDownloadOpenIcon } from '@sketchnote/icons';
import { Button } from '@sketchnote/shared-components';
import { convertArrayToCSV } from 'convert-array-to-csv';
import { format, parseISO } from 'date-fns';
import fileDownload from 'js-file-download';
import { useEffect } from 'react';
import { useBoardStore } from 'src/features/boards';
import { formatDateLight } from 'src/features/kanban/component/Item';
import { calculateTimeInHours } from 'src/utils/date-time-functions';

import { LogUser, useTimesheetStore } from '../store/useTimesheetStore';

export const DownloadTimesheet = () => {
  const boardsStore = useBoardStore();
  const { getTimesheet, timelogs } = useTimesheetStore();

  let totalTimeTracked = 0;

  const getTotalTimeTracked = (logs: LogUser[]): string => {
    if (logs?.length) {
      for (const item of logs) {
        let total = 0;
        if (item?.logs) {
          for (const log of item?.logs) {
            total +=
              parseInt(log?.duration?.hours || '0') * 60 + parseInt(log?.duration?.minutes || '0');
          }
        }
        totalTimeTracked += total;
      }
    }
    if (totalTimeTracked !== 0)
      return calculateTimeInHours({ time: totalTimeTracked, format: 'string' });
    else return '';
  };

  const getBillableHours = (logs: LogUser[]): { billable: string; nonBillable: string } => {
    let billable = '',
      nonBillable = '';
    let billableHours = 0;
    let nonbillableHours = 0;
    if (logs?.length) {
      for (const item of logs) {
        if (item?.logs) {
          for (const log of item?.logs) {
            if (log.billable) {
              billableHours +=
                parseInt(log?.duration?.hours || '0') * 60 +
                parseInt(log?.duration?.minutes || '0');
            } else {
              nonbillableHours +=
                parseInt(log?.duration?.hours || '0') * 60 +
                parseInt(log?.duration?.minutes || '0');
            }
          }
        }
      }
    }
    if (billableHours !== 0)
      billable = calculateTimeInHours({ time: billableHours, format: 'string' });
    if (nonbillableHours !== 0)
      nonBillable = calculateTimeInHours({ time: nonbillableHours, format: 'string' });

    return { billable, nonBillable };
  };

  const handleDates = (values, dateFormat = 'do MMM yyyy') => {
    if (Array.isArray(values)) values = values.map((v) => parseISO(v));
    if (!values) return '';
    if (values.length === 1) return `${format(values[0], dateFormat)}`;
    if (values.length > 1)
      return `${format(values[0], dateFormat)} - ${format(values[1], dateFormat)}`;
    return '';
  };

  const formCsvData = (resultData: any[] = []): any => {
    const csvHeader: string[] = [];
    const csvValues: any[] = [];

    if (resultData) {
      csvHeader.push('Task title');
      csvHeader.push('Task key');
      csvHeader.push('Assigned date');
      csvHeader.push('Assigned to');
      csvHeader.push('Start date');
      csvHeader.push('Estimated time');
      csvHeader.push('Total time tracked');
      csvHeader.push('Non billable hours');
      csvHeader.push('Billable hours');
      csvHeader.push('watch task');

      resultData.map((item) => {
        const values: any[] = [];
        const assignedTo: any[] = [];
        const id = item._id;
        values.push(item.name);
        const key = `${item.key}#${item.number}`;
        values.push(key);
        values.push(handleDates(item.assignDate));
        item.assignee &&
          item.assignee.map((obj) => {
            assignedTo.push(`${obj.firstname} ${obj.lastname}`);
          });
        const result = assignedTo.join(',');
        values.push(`${result}`);
        // push blank as start date is only available for logs and not for the task
        values.push('');
        item.total_estimate ? values.push(`${item.total_estimate}h`) : values.push(0);
        const timeTrackedStr = getTotalTimeTracked(item.logs);
        const { billable, nonBillable } = getBillableHours(item.logs);
        values.push(timeTrackedStr);
        values.push(billable);
        values.push(nonBillable);
        values.push(item.watch);
        csvValues.push(values);

        item.logs.map((timeLog) => {
          if (timeLog.logs) {
            timeLog.logs.map((log) => {
              const logDetails: any[] = [];
              logDetails.push('');
              logDetails.push(`${item.key}#${item.number}`);
              logDetails.push(handleDates(timeLog.assignDate));
              logDetails.push(`${timeLog.firstname} ${timeLog.lastname}`);
              logDetails.push(formatDateLight([log.startDate]));
              logDetails.push(timeLog.estimate ? `${timeLog.estimate}h` : '');
              log?.duration?.minutes
                ? logDetails.push(`${log?.duration?.hours}h ${log?.duration?.minutes}m`)
                : logDetails.push(`${log?.duration?.hours}h`);
              if (log.billable) {
                logDetails.push('');
                log?.duration?.minutes
                  ? logDetails.push(`${log?.duration?.hours}h ${log?.duration?.minutes}m`)
                  : logDetails.push(`${log?.duration?.hours}h`);
              } else {
                log?.duration?.minutes
                  ? logDetails.push(`${log?.duration?.hours}h ${log?.duration?.minutes}m`)
                  : logDetails.push(`${log?.duration?.hours}h`);
                logDetails.push('');
              }
              logDetails.push('');
              csvValues.push(logDetails);
            });
          }
        });
      });

      return { header: csvHeader, values: csvValues };
    }
  };

  const downloadTimesheet = () => {
    if (timelogs.length > 0) {
      const { header, values } = formCsvData(timelogs);
      const csv = convertArrayToCSV(values, { header, separator: ',' });
      fileDownload(csv, 'timesheet.csv');
    }
  };

  useEffect(() => {
    if (boardsStore.id) getTimesheet(boardsStore.id);
  }, [boardsStore.id]);

  return (
    <Button className="ml-16" status="ghost" onClick={downloadTimesheet}>
      <CloudDownloadOpenIcon fill="none" size={24} /> <span className="ml-8">Export</span>
    </Button>
  );
};
